// Rooms & guests picker — the control where the supplier's traveller model
// becomes visible, so it is worth understanding before changing it.
//
// A hotel is priced **per room**, and a room's rate carries its own head count.
// So the search payload is one `occupancy` entry per room, and children are a
// list of **ages** rather than a count, because rates are age-priced and a child
// with no age cannot be quoted at all.
//
//     [{adults: 2, children: [5, 9]}, {adults: 1}]
//      └── room 1: two adults, a 5yo and a 9yo   └── room 2: one adult
//
// Room ORDER is significant: the index is what `occupancy_number` refers to when
// guest names are attached later, so rooms are appended and removed from the end
// rather than reordered.
//
// The downstream consequence is stated in the footnote this renders: however
// many people are in a room, the supplier takes exactly ONE name per room. The
// rest are collected as occupants on the guests page. Saying so here, at the
// point the user chooses room count, is what stops "why does it only want one
// name?" on the next screen.

import type { Occupancy } from "../lib/hotelTypes";

const MAX_ROOMS = 9;
const MAX_ADULTS_PER_ROOM = 9;
const MAX_CHILDREN_PER_ROOM = 6;
const MAX_CHILD_AGE = 17;
const DEFAULT_CHILD_AGE = 8;

interface Props {
  occupancies: Occupancy[];
  onChange: (occupancies: Occupancy[]) => void;
}

export function RoomsPicker({ occupancies, onChange }: Props) {
  function patchRoom(index: number, patch: Partial<Occupancy>) {
    onChange(occupancies.map((o, i) => (i === index ? { ...o, ...patch } : o)));
  }

  function addRoom() {
    if (occupancies.length >= MAX_ROOMS) return;
    onChange([...occupancies, { adults: 2 }]);
  }

  function removeRoom(index: number) {
    if (occupancies.length <= 1) return;
    onChange(occupancies.filter((_, i) => i !== index));
  }

  function setChildCount(index: number, count: number) {
    const current = occupancies[index].children || [];
    // Keep the ages already entered; only the tail changes. Rebuilding the whole
    // list would silently reset a 14-year-old to the default every time someone
    // nudged the count.
    const next = count > current.length
      ? [...current, ...Array(count - current.length).fill(DEFAULT_CHILD_AGE)]
      : current.slice(0, count);
    patchRoom(index, { children: next.length ? next : undefined });
  }

  function setChildAge(index: number, childIndex: number, age: number) {
    const current = [...(occupancies[index].children || [])];
    current[childIndex] = age;
    patchRoom(index, { children: current });
  }

  return (
    <div className="field-popover rooms-popover" onClick={(e) => e.stopPropagation()}>
      {occupancies.map((room, index) => (
        <div className="rooms-row" key={index}>
          <div className="rooms-row-head">
            <span className="rooms-row-title">Room {index + 1}</span>
            {occupancies.length > 1 && (
              <button type="button" className="rooms-remove" onClick={() => removeRoom(index)}>
                Remove
              </button>
            )}
          </div>

          <Stepper
            label="Adults"
            value={room.adults}
            min={1}
            max={MAX_ADULTS_PER_ROOM}
            onChange={(v) => patchRoom(index, { adults: v })}
          />
          <Stepper
            label="Children"
            value={room.children?.length || 0}
            min={0}
            max={MAX_CHILDREN_PER_ROOM}
            onChange={(v) => setChildCount(index, v)}
          />

          {!!room.children?.length && (
            <div className="rooms-ages">
              <span className="rooms-ages-label">
                Age at check-in {/* Not optional: the rate is priced on it. */}
              </span>
              <div className="rooms-ages-grid">
                {room.children.map((age, childIndex) => (
                  <select
                    key={childIndex}
                    value={age}
                    aria-label={`Child ${childIndex + 1} age, room ${index + 1}`}
                    onChange={(e) => setChildAge(index, childIndex, parseInt(e.target.value, 10))}
                  >
                    {Array.from({ length: MAX_CHILD_AGE + 1 }, (_, a) => (
                      <option key={a} value={a}>{a === 0 ? "<1" : a}</option>
                    ))}
                  </select>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {occupancies.length < MAX_ROOMS && (
        <button type="button" className="rooms-add" onClick={addRoom}>
          + Add another room
        </button>
      )}

      <p className="rooms-note">
        Hotels take <b>one guest name per room</b> — everyone else in the room is
        added as an occupant when you enter guest details.
      </p>
    </div>
  );
}

function Stepper(props: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number;
}) {
  const min = props.min ?? 0;
  const max = props.max ?? 9;
  return (
    <div className="field-popover-row">
      <span className="label">{props.label}</span>
      <span className="stepper">
        <button
          type="button"
          disabled={props.value <= min}
          onClick={() => props.onChange(Math.max(min, props.value - 1))}
        >
          −
        </button>
        <span className="n">{props.value}</span>
        <button
          type="button"
          disabled={props.value >= max}
          onClick={() => props.onChange(Math.min(max, props.value + 1))}
        >
          +
        </button>
      </span>
    </div>
  );
}
